/**
 * Search builder — the categorized "add criterion" popover. Searchable list of
 * every catalog kind, grouped (Pipeline / Medical / Forms / Engagement); picking
 * one hands a defaulted condition back to the caller.
 */
import { useEffect, useRef, useState } from 'react';
import { Plus } from 'lucide-react';
import { outlineBtnCls, fieldCls } from '../filters';
import { Z } from '../ui';
import { KINDS, KIND_CATEGORIES, defaultConditionFor } from '../../pages/search/searchLib';
import type { SearchCondition } from '../../types';

interface Props {
  onPick: (condition: SearchCondition) => void;
  label?: string;
  /** Render as a small text button instead of the outline button. */
  compact?: boolean;
}

export default function CriterionPalette({ onPick, label = 'Add step', compact = false }: Props) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const term = q.trim().toLowerCase();
  const matches = (k: (typeof KINDS)[number]) =>
    !term || k.label.toLowerCase().includes(term) || k.hint.toLowerCase().includes(term) || k.kind.includes(term);

  return (
    <div ref={ref} className="relative">
      {compact ? (
        <button
          type="button"
          onClick={() => setOpen((s) => !s)}
          aria-expanded={open}
          className="inline-flex items-center gap-1 text-xs font-medium text-primary-600 hover:underline dark:text-primary-400"
        >
          <Plus className="h-3.5 w-3.5" />
          {label}
        </button>
      ) : (
        <button type="button" onClick={() => setOpen((s) => !s)} aria-expanded={open} className={outlineBtnCls(false)}>
          <Plus className="h-4 w-4" />
          {label}
        </button>
      )}
      {open && (
        <div
          role="dialog"
          aria-label="Pick a criterion"
          // Z.header, not Z.menu: this popover must clear the sticky builder chrome above it.
          className={`absolute left-0 ${Z.header} mt-2 max-h-[26rem] w-80 max-w-[calc(100vw-2rem)] overflow-y-auto rounded-lg border border-gray-200 bg-white p-2 shadow-lg dark:border-gray-700 dark:bg-gray-800`}
        >
          <input
            autoFocus
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search criteria…"
            aria-label="Search criteria"
            className={`${fieldCls} mb-2 w-full`}
          />
          {KIND_CATEGORIES.map((cat) => {
            const kinds = KINDS.filter((k) => k.category === cat.key && matches(k));
            if (!kinds.length) return null;
            return (
              <div key={cat.key} className="mb-1.5">
                <div className="px-2 pb-0.5 pt-1 text-[10px] font-semibold uppercase tracking-widest text-gray-400 dark:text-gray-500">
                  {cat.label}
                </div>
                {kinds.map((k) => (
                  <button
                    key={k.kind}
                    type="button"
                    onClick={() => { setOpen(false); setQ(''); onPick(defaultConditionFor(k.kind)); }}
                    className="block w-full rounded-md px-2 py-1.5 text-left transition-colors duration-150 hover:bg-gray-50 focus:bg-gray-50 focus:outline-none dark:hover:bg-gray-700 dark:focus:bg-gray-700"
                  >
                    <span className="block text-sm font-medium text-gray-800 dark:text-gray-100">{k.label}</span>
                    <span className="block truncate text-xs text-gray-400 dark:text-gray-500">{k.hint}</span>
                  </button>
                ))}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
